import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Eye, Sparkles, TrendingUp } from 'lucide-react';
import { ContentIdea } from '@/types/dashboard';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import useEmblaCarousel from 'embla-carousel-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface AllIdeasSliderProps {
  ideas: ContentIdea[];
  onIdeaClick: (idea: ContentIdea) => void;
}

export const AllIdeasSlider = ({ ideas, onIdeaClick }: AllIdeasSliderProps) => {
  const { t, language } = useLanguage();
  
  // Sort ideas by creation date, latest first
  const sortedIdeas = [...ideas].sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const getViralScoreColor = (score: number) => {
    const normalizedScore = Math.max(0, Math.min(100, score));
    const hue = (normalizedScore / 100) * 120;
    return `hsl(${hue}, 70%, 45%)`;
  };

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false,
    align: 'center',
    slidesToScroll: 1,
    containScroll: 'trimSnaps'
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const scrollPrev = () => {
    emblaApi?.scrollPrev();
  };

  const scrollNext = () => {
    emblaApi?.scrollNext();
  };

  // Update button states
  const onSelect = () => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  };

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  if (sortedIdeas.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-bold text-foreground">{t('library.allIdeas')}</h3>
              <p className="text-sm text-muted-foreground">
                {sortedIdeas.length} {sortedIdeas.length === 1 
                  ? (language === 'pl' ? 'pomysł' : 'idea')
                  : (language === 'pl' ? (sortedIdeas.length < 5 ? 'pomysły' : 'pomysłów') : 'ideas')
                } {language === 'pl' ? 'dostępnych' : 'available'}
              </p>
            </div>
          </div>
        </div>

        <div className="relative">
          {/* Left Navigation Button */}
          <Button
            variant="default"
            size="icon"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-12 w-12 md:h-14 md:w-14 rounded-full shadow-xl bg-primary hover:bg-primary/90 text-white disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" />
          </Button>

          {/* Right Navigation Button */}
          <Button
            variant="default"
            size="icon"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-12 w-12 md:h-14 md:w-14 rounded-full shadow-xl bg-primary hover:bg-primary/90 text-white disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-6 w-6 md:h-7 md:w-7" />
          </Button>

        <div className="overflow-hidden px-10 md:px-16" ref={emblaRef}>
          <div className="flex gap-4">
            {sortedIdeas.map((idea) => {
              const viralScore = idea.viral_score || 0;
              const borderColor = getViralScoreColor(viralScore);
              
              return (
              <div
                key={idea.id}
                className="flex-[0_0_100%] min-w-0 md:flex-[0_0_calc(100%-1rem)]"
              >
                <Card 
                  className="hover:shadow-lg h-full flex flex-col transition-all"
                  style={{ 
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: borderColor
                  }}
                >
                  <CardContent className="p-4 md:p-6 flex-1 flex flex-col">
                    <div className="space-y-2 flex-1 flex flex-col">
                      {/* Title - Most Important - Fixed Height */}
                      <h4 className="text-xl md:text-2xl font-bold text-foreground leading-tight line-clamp-2 h-[56px] md:h-[60px]">
                        {idea.title}
                      </h4>

                      {/* Metadata - Compact */}
                      <div className="flex items-center justify-between gap-2 h-[22px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          {idea.viral_score !== null && idea.viral_score !== undefined && (
                             <div className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" style={{ color: borderColor }} />
                              <span className="font-semibold text-xs" style={{ color: borderColor }}>{viralScore}/100</span>
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(idea.created_at), 'd MMM yyyy', { locale: language === 'pl' ? pl : enUS })}
                        </span>
                      </div>

                      {/* Description - Fixed Height */}
                      <div className="h-[42px]">
                        {idea.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {idea.description}
                          </p>
                        )}
                      </div>

                      {/* Source - Compact - Fixed Height */}
                      <div className="h-[22px]">
                        {idea.source_competitor && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{t('library.basedOn')}</span>
                            <Badge variant="outline" className="text-xs">
                              @{idea.source_competitor.username}
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* View Button - Smaller - at bottom */}
                      <div className="mt-auto pt-2">
                        <Button
                          onClick={() => onIdeaClick(idea)}
                          className="w-full bg-primary hover:bg-primary/90 text-white h-9 text-sm"
                          size="sm"
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          {t('library.viewIdea')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              );
            })}
          </div>
        </div>
        </div>
      </CardContent>
    </Card>
  );
};
